import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { cleanEmail } from '@makyoapp/makyo-common'
import {
  BuildingAlt16,
  ChevronDown,
  ChevronUp,
  Logout24,
  Moon24,
  Plus16,
  Sun24,
  SunMoon24,
} from '@makyoapp/makyo-icon/icon'
import {
  AppIcon,
  AvatarCell,
  AvatarSize,
  Button,
  ButtonVariant,
  DD,
  DDItem,
  FallbackAvatar,
  JustifyVariant,
  ShadowVariant,
  ShapeVariant,
  SingleIconSelect,
  ThemeMode,
  useTheme,
} from '@makyoapp/makyo-ui'

import { APP_VERSION } from '@/configs'
import { useAppContext } from '@/context/AppContext'
import { useCacheContext } from '@/context/CacheContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useAddOrgCb } from '@/hooks/useAddOrgCb'
import useAuth from '@/hooks/useAuth'
import { ORG_APP_ROUTE, ORG_ROUTE, PROJECT_ROUTE, USER_ORG_ROUTE, USER_ROUTE } from '@/shared/constants'

import { NewOrgTeamModalForm } from '../NewOrgTeamModalForm'
import { UploadUserAvatar } from '../UploadAvatar'

/**
 * handle showing the user/org menu for toolbar
 */
export function OrgMenu() {
  const { t } = useTranslation()

  const { orgs, recentApps, showCreateOrg, setShowCreateOrg } = useOrgsContext()

  const location = useLocation()
  const { project } = useAppContext()
  const splitRoute = location.pathname.split('/')
  const isProject = location.pathname.startsWith(PROJECT_ROUTE)
  const orgId = isProject ? project?.orgId : splitRoute?.[2]
  const currentOrg = orgs.find((o) => o.id === orgId)

  const { logout, userData } = useAuth()

  const { imageCache } = useCacheContext()
  const withCache = !!currentOrg && !!imageCache.values[`org-${currentOrg.id}`]

  const handleAddOrg = useAddOrgCb(() => {
    setShowCreateOrg(false)
  })

  const [show, setShow] = useBoolean(false)

  useEffect(() => {
    const cb = (e: any) => {
      if (e?.target && e.target.closest?.('#user-menu-ddm')) {
        if (
          e.target.className.includes('dd-menu-item-inner') ||
          e.target.closest?.('.dd-menu-item-inner') !== null ||
          e.target.id === 'user-menu-goto-manage'
        ) {
          setShow(false)
        }
      }
    }
    window.addEventListener('click', cb)

    return () => {
      window.removeEventListener('click', cb)
    }
  }, [setShow])
  const { state: themeState, setState: setThemeState } = useTheme()

  // Add timestamp state for cache busting
  const [timestamp] = useState<number>(Date.now())

  // Create timestamped avatar URL
  const userAvatarWithTimestamp =
    userData?.avatar && !userData.avatar.startsWith('blob:') ? `${userData.avatar}?t=${timestamp}` : userData?.avatar

  return (
    <DD
      menuAlwaysRender={false}
      show={show}
      onToggle={() => setShow(!show)}
      id='user-menu'
      className='w-full h-full'
      menuClassName={clsx('mx-2 pt-2 dd-menu')}
      withNotch={true}
      notchClassName={clsx('bg-slate-900', 'mk-notch-pos')}
      placement={'bottom-end'}
      toggle={(active) => (
        <div className='flex-c w-full text-slate-300'>
          <div className={clsx('relative w-flex justify-start', currentOrg ? 'w-12' : '')}>
            <FallbackAvatar
              text={cleanEmail(userData?.email || '')}
              src={userAvatarWithTimestamp}
              email={userData?.email}
              faceAvatar={true}
              alt={userData?.name || userData?.email}
            />
            {currentOrg && (
              <>
                <FallbackAvatar
                  className={clsx(
                    'bottom-0 right-0 left-auto rtl:left-0 rtl:right-auto absolute',
                    'border-3 border-zinc-800 dark:ring-slate-800',
                    'bg-slate-800'
                  )}
                  size={AvatarSize.S}
                  text={currentOrg.name}
                  src={currentOrg.avatar}
                  alt={currentOrg.name}
                  withCache={withCache}
                />
              </>
            )}
          </div>
          <div>{!active ? <ChevronDown /> : <ChevronUp />}</div>
        </div>
      )}
      toggleClassName='dd-menu-toggle'
      menuNoPad={true}
      menuShadow={ShadowVariant.XL}
      menuDefaultSize={false}
      menuWrapperClassName={clsx('pb-2', 'overflow-y-auto max-h-[88vh] mk-scroll')}
    >
      <div className='w-full py-4 flex-cc flex-col'>
        <UploadUserAvatar
          id='user-menu-upload-avatar'
          size={AvatarSize.XL2}
          btnStyleBorder={false}
          btnIconWrapperClassName='border-4 border-zinc-800 dark:border-slate-900'
          onBeforeUpdate={() => setShow(true)}
        />
        <div className='pt-2'>{userData?.name}</div>
        <div className='pb-2 toolbar-text-light'>{userData?.email}</div>
        <div className='dark'>
          {/*
            <Button
              id='user-menu-goto-manage'
              variant={ButtonVariant.Outlined}
              shape={ShapeVariant.Circle}
              onClick={() => {
                navigate(USER_ROUTE)
              }}
            >
              {t('Manage your Makyo account')}
            </Button>
          */}
          <Link id='user-menu-goto-manage' className='user-menu-goto-manage btn-outlined rounded-full' to={USER_ROUTE}>
            {t('Manage your Makyo account')}
          </Link>
        </div>
      </div>
      {recentApps.length > 0 && (
        <>
          <hr className='dd-menu-hr pb-2 mt-1' />
          <span className='dd-menu-title toolbar-text-light'>{t('Recent Apps')}</span>
        </>
      )}
      {recentApps.slice(0, 5).map((recentApp) => {
        return (
          <Link key={`app-${recentApp.id}`} to={`${PROJECT_ROUTE}/${recentApp.id}`} className='dd-menu-row'>
            <div className='dd-menu-item-inner'>
              <AppIcon
                defaultSize={false}
                icon={recentApp.icon}
                shape={ShapeVariant.Square}
                size={AvatarSize.XS}
                noPad={true}
                className={'rounded-md [&_img]:rounded-md [&_img]:object-cover [&_img]:w-full'}
                style={{
                  background: recentApp.iconBgColor,
                }}
              />
              <div className='txt'>{recentApp.title}</div>
            </div>
          </Link>
        )
      })}
      <hr className='dd-menu-hr pb-2 mt-1' />
      <span className='dd-menu-title toolbar-text-light'>{t('Recent Organizations')}</span>
      {orgs
        .sort((a, b) => {
          return b.accessed.getTime() - a.accessed.getTime()
        })
        .slice(0, 3)
        .map((org) => {
          return (
            <Link to={`${ORG_ROUTE}/${org.id}/${ORG_APP_ROUTE}`} key={`org-${org.name}`} className={'dd-menu-row'}>
              <div className='dd-menu-item-inner'>
                <AvatarCell {...org} size={AvatarSize.XS} />
              </div>
            </Link>
          )
        })}
      <div className='flex-cc'>
        <Link className='dd-menu-row' to={`${USER_ROUTE}/${USER_ORG_ROUTE}`}>
          <div className='dd-menu-item-inner'>
            <div className='w-6 flex-cc'>
              <BuildingAlt16 width={20} height={20} />
            </div>
            <span>{t('My Organizations')}</span>
          </div>
        </Link>
        <div className='dark ltr:pr-4 rtl:pl-4'>
          <Button
            id='user-menu-new-org'
            className='flex-cc uppercase text-m-sm py-1 px-1.5'
            defaultFontStyle={false}
            defaultSize={false}
            noPadding={true}
            variant={ButtonVariant.Outlined}
            onClick={() => {
              setShowCreateOrg(true)
            }}
          >
            <Plus16 />
            <div>{t('New')}</div>
          </Button>
          <NewOrgTeamModalForm
            formId='new-org'
            onSubmit={handleAddOrg}
            onHide={() => setShowCreateOrg(false)}
            show={showCreateOrg}
          />
        </div>
      </div>
      <hr className='dd-menu-hr pb-2 mt-1' />
      <DDItem
        id='user-theme-mode'
        className='dd-menu-row'
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className='dd-menu-item-inner flex-c w-full'>
          <div className='w-full'>
            <Trans>Theme</Trans>
          </div>
          <SingleIconSelect
            id='appearance-scheme'
            justify={JustifyVariant.Start}
            optionClassName='text-slate-600 dark:text-white'
            onClick={(e) => {
              if (e.id === themeState.mode) {
                return
              }

              setThemeState({
                mode: e.id as ThemeMode,
              })
            }}
            selected={themeState.mode}
            options={[
              {
                icon: () => <Sun24 />,
                title: t('Light'),
                id: 'light',
              },
              {
                icon: () => <Moon24 />,
                title: t('Dark'),
                id: 'dark',
              },
              {
                icon: () => <SunMoon24 />,
                title: t("Match device's setting"),
                id: 'system',
              },
            ]}
          />
        </div>
      </DDItem>
      <DDItem id='user-menu-logout' className='dd-menu-row' onClick={logout}>
        <div className='dd-menu-item-inner'>
          <Logout24 width={20} height={20} />
          <span>{t('Logout')}</span>
        </div>
      </DDItem>
      <div className='flex justify-center text-light text-m-sm px-4'>v{APP_VERSION}</div>
    </DD>
  )
}
